import { Box } from '@chakra-ui/react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useRouter } from 'next/router';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { AppLoader } from '~/components/AppLoader';
import { useGetMediaKit } from '~/hooks/api/media-kits/get';

import { useInitPageBuilderStore } from '../hooks/useInitPageBuilderStore';
import { usePageBuilderStore } from '../hooks/usePageBuilderStore';
import { AnyBlock } from '../types';
import { BuilderItem } from './BuilderItem';

export const BuilderCanvas = memo(() => {
  const router = useRouter();
  useInitPageBuilderStore();

  const id = router.query.id as string;
  const { status, error } = useGetMediaKit({ id });
  const { blocks, setBlocks } = usePageBuilderStore();
  const [activeId, setActiveId] = useState<string | null>(null);

  const parentRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(1200);

  // Calculate scale factor for auto zoom-out when container is smaller than 1200px
  const CONTAINER_WIDTH = 1200;
  const scale =
    containerWidth < CONTAINER_WIDTH ? containerWidth / CONTAINER_WIDTH : 1;

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect;
        setContainerWidth(width);
      }
    });

    if (parentRef.current) {
      resizeObserver.observe(parentRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [parentRef]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveId(null);
      const { active, over } = event;

      if (over && active.id !== over.id) {
        setBlocks((prevBlocks: AnyBlock[]) => {
          const oldIndex = prevBlocks.findIndex(
            (block) => block.id === active.id,
          );
          const newIndex = prevBlocks.findIndex(
            (block) => block.id === over.id,
          );

          const newBlocks = [...prevBlocks];
          const [removed] = newBlocks.splice(oldIndex, 1);
          newBlocks.splice(newIndex, 0, removed);

          return newBlocks;
        });
      }
    },
    [setBlocks],
  );

  const activeBlock = blocks.find((block) => block.id === activeId);

  if (status === 'loading') {
    return <AppLoader />;
  }

  if (error || !id) {
    // Handle error state appropriately
    return <Box>Error loading media kit</Box>;
  }

  return (
    <Box
      ref={parentRef}
      backgroundColor="var(--media-kit-background-primary)"
      color="var(--media-kit-text-base)"
      height="100%"
      padding={{ base: 0, lg: 8 }}
      transition="background-color 80ms linear"
    >
      <Box
        maxWidth={1200}
        width="100%"
        mx="auto"
        sx={{
          zoom: scale,
          transition: 'zoom 200ms ease-out',
        }}
      >
        <DndContext
          modifiers={[restrictToVerticalAxis]}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={blocks.map((block) => block.id)}
            strategy={verticalListSortingStrategy}
          >
            <Box display="flex" flexDirection="column" gap={2}>
              {blocks.map((block: AnyBlock) => (
                <BuilderItem
                  key={block.id}
                  block={block}
                  isDragging={activeId === block.id}
                />
              ))}
            </Box>
          </SortableContext>

          <DragOverlay>
            {activeBlock ? <BuilderItem block={activeBlock} isOverlay /> : null}
          </DragOverlay>
        </DndContext>
      </Box>
    </Box>
  );
});
